/**
 * LocalStorage utility for data persistence
 * Provides safe storage and retrieval with error handling
 */

const STORAGE_PREFIX = 'cardgen_'

/**
 * Get a namespaced storage key
 */
function getStorageKey(key) {
  return `${STORAGE_PREFIX}${key}`
}

/**
 * Save data to localStorage
 * @param {string} key - Storage key
 * @param {any} data - Data to save (will be JSON stringified)
 * @returns {boolean} - Success status
 */
export function saveToStorage(key, data) {
  try {
    const storageKey = getStorageKey(key)
    const serialized = JSON.stringify({
      data,
      timestamp: new Date().toISOString()
    })
    localStorage.setItem(storageKey, serialized)
    return true
  } catch (error) {
    console.error('Error saving to localStorage:', error)
    // Handle quota exceeded error
    if (error.name === 'QuotaExceededError') {
      console.warn('LocalStorage quota exceeded. Clearing old data...')
      clearOldStorage()
      // Try once more
      try {
        const storageKey = getStorageKey(key)
        const serialized = JSON.stringify({
          data,
          timestamp: new Date().toISOString()
        })
        localStorage.setItem(storageKey, serialized)
        return true
      } catch (retryError) {
        console.error('Failed to save after clearing old data:', retryError)
        return false
      }
    }
    return false
  }
}

/**
 * Load data from localStorage
 * @param {string} key - Storage key
 * @param {any} defaultValue - Default value if not found
 * @returns {any} - Retrieved data or default value
 */
export function loadFromStorage(key, defaultValue = null) {
  try {
    const storageKey = getStorageKey(key)
    const item = localStorage.getItem(storageKey)
    
    if (!item) {
      return defaultValue
    }
    
    const parsed = JSON.parse(item)
    return parsed.data !== undefined ? parsed.data : defaultValue
  } catch (error) {
    console.error('Error loading from localStorage:', error)
    return defaultValue
  }
}

/**
 * Remove data from localStorage
 * @param {string} key - Storage key
 */
export function removeFromStorage(key) {
  try {
    const storageKey = getStorageKey(key)
    localStorage.removeItem(storageKey)
  } catch (error) {
    console.error('Error removing from localStorage:', error)
  }
}

/**
 * Clear all app-related localStorage data
 */
export function clearAllStorage() {
  try {
    const keys = Object.keys(localStorage)
    keys.forEach(key => {
      if (key.startsWith(STORAGE_PREFIX)) {
        localStorage.removeItem(key)
      }
    })
  } catch (error) {
    console.error('Error clearing localStorage:', error)
  }
}

/**
 * Clear old storage entries (older than 30 days)
 */
function clearOldStorage() {
  try {
    const keys = Object.keys(localStorage)
    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000)
    
    keys.forEach(key => {
      if (key.startsWith(STORAGE_PREFIX)) {
        try {
          const item = localStorage.getItem(key)
          if (item) {
            const parsed = JSON.parse(item)
            if (parsed.timestamp) {
              const timestamp = new Date(parsed.timestamp).getTime()
              if (timestamp < thirtyDaysAgo) {
                localStorage.removeItem(key)
              }
            }
          }
        } catch (e) {
          // If we can't parse it, remove it
          localStorage.removeItem(key)
        }
      }
    })
  } catch (error) {
    console.error('Error clearing old storage:', error)
  }
}

/**
 * Check if localStorage is available
 * @returns {boolean}
 */
export function isStorageAvailable() {
  try {
    const test = '__storage_test__'
    localStorage.setItem(test, test)
    localStorage.removeItem(test)
    return true
  } catch (e) {
    return false
  }
}

/**
 * Get storage size estimate (approximate)
 * @returns {number} - Size in bytes
 */
export function getStorageSize() {
  try {
    let total = 0
    const keys = Object.keys(localStorage)
    keys.forEach(key => {
      if (key.startsWith(STORAGE_PREFIX)) {
        total += localStorage.getItem(key).length
      }
    })
    return total
  } catch (error) {
    console.error('Error calculating storage size:', error)
    return 0
  }
}






